// Shared static data for the Journal (/blog) list and article pages.
// Used by both the page components and TanStack route head() functions
// so server-rendered metadata and client-rendered content stay in sync.

import imgVriddhi from "@/assets/blog/vriddhi-vision.jpg";
import imgAiVision from "@/assets/blog/ai-vision-scrap.jpg";
import imgEscrowSecure from "@/assets/blog/escrow-secure.jpg";
import imgFoundryDirect from "@/assets/blog/foundry-direct.jpg";
import imgHms1 from "@/assets/blog/hms1-yard.jpg";
import imgSpongeIron from "@/assets/blog/sponge-iron.jpg";
import imgMsBillets from "@/assets/blog/ms-billets.jpg";
import imgRaipur from "@/assets/blog/raipur-billet-prices.jpg";
import imgEscrowPdc from "@/assets/blog/escrow-vs-pdc.jpg";
import imgHmsVolatility from "@/assets/blog/hms1-volatility.jpg";
import imgEscrowCompliance from "@/assets/blog/escrow-compliance.jpg";
import imgMaldaAsansol from "@/assets/blog/malda-asansol.jpg";
import imgAiYardUpgrade from "@/assets/blog/ai-yard-upgrade.jpg";
import imgGstDigital from "@/assets/blog/gst-digital-invoicing.jpg";
import imgAiQualityDisputes from "@/assets/blog/ai-quality-disputes.jpg";
import imgWorkingCapital from "@/assets/blog/working-capital-payments.jpg";
import imgCircularEconomy from "@/assets/blog/circular-economy-india.jpg";
import imgIronVolatility2026 from "@/assets/blog/iron-price-volatility-2026.jpg";
import imgMarketOrganizing from "@/assets/blog/india-scrap-market-organizing.jpg";
import imgEmergingHubs from "@/assets/blog/emerging-foundry-hubs.jpg";
import imgEprBrokerage from "@/assets/blog/epr-credit-brokerage.jpg";

export type BlogListPost = {
  slug: string;
  title: string;
  snippet: string;
  date: string;
  readTime: string;
  category: string;
  gradient: string;
  image?: string;
  /** Optional: shows two extra links — to the About FAQ and a marketplace search. */
  materialLinks?: { aboutFaq: string; marketplaceQuery: string; materialLabel: string };
};

export const BLOG_FEATURED: BlogListPost = {
  slug: "metal-linked",
  title: "Metal Linked: Redefining Growth and Abundance in India's Metal Trade",
  snippet:
    "An ancient invocation — 'Let there be growth, let there be abundance' — becomes the operating blueprint for trust, escrow security and shared prosperity in India's B2B metal trade.",
  date: "April 23, 2026",
  readTime: "6 min read",
  category: "Philosophy & Vision",
  gradient: "from-primary via-primary/80 to-accent",
  image: imgVriddhi,
};

export const BLOG_POSTS: BlogListPost[] = [
  {
    slug: "epr-credit-brokerage-india-guide",
    title: "EPR Credit Brokerage in India: The 2026 Compliance Playbook for PIBOs",
    snippet:
      "Plastic, e-waste, battery, tyre and used oil obligations explained — how EPR certificates are generated, what credits cost per tonne, and how to close targets without Environmental Compensation.",
    date: "June 13, 2026",
    readTime: "8 min read",
    category: "Compliance · EPR",
    gradient: "from-success/30 to-accent/20",
    image: imgEprBrokerage,
    materialLinks: {
      aboutFaq: "/epr#epr-faq",
      marketplaceQuery: "/epr",
      materialLabel: "EPR credits",
    },
  },
  {
    slug: "mid-year-market-report-iron-scrap-volatility-2026",
    title: "Mid-Year Market Report: Navigating Iron Scrap Price Volatility in 2026",
    snippet:
      "Domestic infrastructure demand is keeping HMS prices firm, but localized supply bottlenecks are causing daily swings. See how AI price benchmarking and escrow protect margins in 2026.",
    date: "April 30, 2026",
    readTime: "5 min read",
    category: "Market Report · Iron Prices",
    gradient: "from-info/30 to-accent/20",
    image: imgIronVolatility2026,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=HMS%201",
      materialLabel: "AI-benchmarked HMS lots",
    },
  },
  {
    slug: "india-scrap-market-organizing-300-mt",
    title: "The Multi-Billion Dollar Shift: Why India's Scrap Metal Market is Finally Organizing",
    snippet:
      "India is targeting 300 MT crude steel by 2030 — and 70% of scrap collection is still unorganized. How LohaLink is becoming the operating system for the secondary steel transition.",
    date: "April 29, 2026",
    readTime: "6 min read",
    category: "Industry Growth · Secondary Steel",
    gradient: "from-success/30 to-primary/20",
    image: imgMarketOrganizing,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "organized scrap network",
    },
  },
  {
    slug: "supplying-indias-emerging-foundry-hubs",
    title: "New Furnaces, New Opportunities: Supplying India's Emerging Foundry Hubs",
    snippet:
      "New EAFs are opening across Eastern India and Western manufacturing corridors. Reach foundries 400 km away with risk-free, escrow-secured pan-India dispatch on LohaLink.",
    date: "April 28, 2026",
    readTime: "5 min read",
    category: "Pan-India Trade · Logistics",
    gradient: "from-warning/30 to-accent/20",
    image: imgEmergingHubs,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer",
      materialLabel: "pan-India foundry demand",
    },
  },
  {
    slug: "malda-asansol-hms1-case-study",
    title: "Case Study: From Malda to Asansol — 50 Tonnes of HMS 1 Sourced in 48 Hours",
    snippet:
      "How a Malda yard owner and an Asansol steel foundry closed a 50-tonne HMS 1 deal on LohaLink — zero brokers, zero quality disputes, zero payment risk, settled the same evening.",
    date: "April 28, 2026",
    readTime: "7 min read",
    category: "Case Study · West Bengal",
    gradient: "from-success/30 to-primary/20",
    image: imgMaldaAsansol,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=HMS%201",
      materialLabel: "HMS 1 lots",
    },
  },
  {
    slug: "rbi-grade-escrow-future-of-bulk-scrap-trading",
    title: "Why RBI-Grade Escrow is the Future of Bulk Scrap Metal Trading in India",
    snippet:
      "\"Maal gaya, paisa kab?\" — the question that defines Indian scrap trading. See how LohaLink's RBI-grade nodal escrow guarantees same-day settlement for sellers and quality assurance for buyers.",
    date: "April 27, 2026",
    readTime: "6 min read",
    category: "Finance · Escrow",
    gradient: "from-success/30 to-accent/20",
    image: imgEscrowSecure,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "escrow-secured trade",
    },
  },
  {
    slug: "bypassing-the-broker-foundry-direct-sourcing",
    title: "Bypassing the Broker: How Foundries Can Source Premium Scrap Directly",
    snippet:
      "Foundry procurement managers are flattening the supply chain. Browse 3,400+ GST-verified yards, AI-benchmarked pricing, and live truck tracking on LohaLink's Foundry Connect.",
    date: "April 26, 2026",
    readTime: "6 min read",
    category: "Procurement · Foundry",
    gradient: "from-accent/30 to-info/20",
    image: imgFoundryDirect,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer",
      materialLabel: "Foundry Connect",
    },
  },
  {
    slug: "gst-digital-invoicing-scrap-traders",
    title: "Demystifying GST for Scrap Metal Traders: How Digital Invoicing is Changing the Game",
    snippet:
      "GSTIN verification, automated e-invoices and seamless ITC claims — how LohaLink takes the compliance headache out of bulk scrap metal trading in India.",
    date: "April 24, 2026",
    readTime: "6 min read",
    category: "Compliance · GST",
    gradient: "from-info/30 to-success/20",
    image: imgGstDigital,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "GST-verified trade",
    },
  },
  {
    slug: "ai-eliminating-quality-disputes-scrap-trade",
    title: "How AI is Eliminating Quality Disputes in the Indian Scrap Trade",
    snippet:
      "Gate rejections and forced downgrades cost the trade crores every year. See how LohaLink's AI Vision replaces subjective grading arguments with objective, photo-verified facts before dispatch.",
    date: "April 23, 2026",
    readTime: "5 min read",
    category: "Technology · Quality Control",
    gradient: "from-primary/30 to-info/20",
    image: imgAiQualityDisputes,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=HMS%201",
      materialLabel: "AI-graded HMS lots",
    },
  },
  {
    slug: "same-day-payments-working-capital-scrap-yards",
    title: "Unlocking Working Capital: Why Same-Day Payments are Critical for Scrap Yards",
    snippet:
      "MSME yard owners lose 30–45 days of cash flow on every deal. Discover how RBI-grade escrow and same-day settlement on LohaLink lets you compound your turnover instead of funding the supply chain.",
    date: "April 21, 2026",
    readTime: "6 min read",
    category: "Finance · Working Capital",
    gradient: "from-success/30 to-primary/20",
    image: imgWorkingCapital,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "same-day settlement",
    },
  },
  {
    slug: "circular-economy-india-organized-scrap-sourcing",
    title: "Fueling India's Circular Economy: The Role of Organized Scrap Metal Sourcing",
    snippet:
      "Recycling one tonne of steel saves 1.5 tonnes of iron ore and cuts CO₂ by 58%. See how LohaLink is building the digital infrastructure powering India's sustainable secondary steel supply chain.",
    date: "April 19, 2026",
    readTime: "6 min read",
    category: "Sustainability · Circular Economy",
    gradient: "from-success/30 to-info/20",
    image: imgCircularEconomy,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer",
      materialLabel: "verified recycled scrap",
    },
  },
  {
    slug: "3-ways-ai-upgrading-indian-scrap-yard",
    title: "3 Ways AI is Upgrading the Indian Scrap Yard",
    snippet:
      "From AI photo categorization to mobile-first 60-second listings and digital LR receipts — how India's scrap yards are leading the digital circular economy.",
    date: "April 25, 2026",
    readTime: "5 min read",
    category: "Technology · AI Vision",
    gradient: "from-primary/30 to-accent/20",
    image: imgAiYardUpgrade,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "Supplier Central",
    },
  },
  {
    slug: "ai-vision-resolving-scrap-quality-disputes",
    title: "AI Vision: Resolving Scrap Quality Disputes Before Dispatch",
    snippet:
      "Tired of grade downgrades upon delivery? Learn how LohaLink uses AI Vision to standardize scrap categorization and eliminate quality disputes before the truck leaves your yard.",
    date: "April 22, 2026",
    readTime: "5 min read",
    category: "Technology · AI Vision",
    gradient: "from-primary/30 to-accent/20",
    image: imgAiVision,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "Supplier Central",
    },
  },
  {
    slug: "why-escrow-is-critical-for-scrap-trade-2026",
    title: "Why Your Metal Business Cannot Afford Another Bounced PDC in 2026",
    snippet:
      "Cash flow is king in 2026. See how LohaLink's RBI-compliant escrow protects scrap aggregators from delayed payments and bounced post-dated cheques.",
    date: "April 20, 2026",
    readTime: "6 min read",
    category: "Finance · Escrow",
    gradient: "from-success/30 to-primary/20",
    image: imgEscrowSecure,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/auth",
      materialLabel: "Verified Seller registration",
    },
  },
  {
    slug: "direct-to-foundry-zero-middlemen-metal-trade",
    title: "Cutting the Fat: Direct-to-Foundry, Zero Middlemen Metal Trade",
    snippet:
      "Stop losing margins to unseen broker commissions. LohaLink connects verified scrap suppliers directly with major foundries in Eastern India for transparent trading.",
    date: "April 17, 2026",
    readTime: "5 min read",
    category: "Marketplace · Direct Trade",
    gradient: "from-accent/30 to-info/20",
    image: imgFoundryDirect,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer",
      materialLabel: "Foundry Connect",
    },
  },
  {
    slug: "hms-1-scrap-buyers-guide-india",
    title: "HMS 1 Scrap in India: Buyer's Guide to Pricing, Specs & Verified Suppliers",
    snippet:
      "Everything foundries need to know about HMS 1 scrap in India — chemistry, density tolerances, current price drivers, and how to source from GST verified scrap dealers with escrow protection.",
    date: "April 18, 2026",
    readTime: "8 min read",
    category: "Materials · HMS 1",
    gradient: "from-primary/30 to-info/20",
    image: imgHms1,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=HMS%201",
      materialLabel: "HMS 1 scrap",
    },
  },
  {
    slug: "sponge-iron-dri-procurement-playbook",
    title: "Sponge Iron (DRI) Procurement Playbook for Indian Induction Furnaces",
    snippet:
      "Coal-based vs gas-based sponge iron, Fe(M) and metallization benchmarks, and how to lock in verified sponge iron suppliers from Durgapur, Raipur and Jharsuguda on a B2B metal scrap marketplace.",
    date: "April 15, 2026",
    readTime: "9 min read",
    category: "Materials · Sponge Iron (DRI)",
    gradient: "from-warning/30 to-accent/20",
    image: imgSpongeIron,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=Sponge%20Iron",
      materialLabel: "Sponge Iron (DRI)",
    },
  },
  {
    slug: "ms-billets-direct-from-mills",
    title: "MS Billets Direct from Mills: Cutting Out Brokers in 2026",
    snippet:
      "How rolling mills are procuring MS billets online directly from manufacturers — IS 2830/2831 specs, size ranges, payment terms, and why escrow-backed orders are replacing PDC trading.",
    date: "April 10, 2026",
    readTime: "7 min read",
    category: "Materials · MS Billets",
    gradient: "from-success/30 to-primary/20",
    image: imgMsBillets,
    materialLinks: {
      aboutFaq: "/about#faq-heading",
      marketplaceQuery: "/buyer?q=MS%20Billets",
      materialLabel: "MS Billets",
    },
  },
  {
    slug: "raipur-billet-prices-q2-2026",
    title: "2026 Q2 Market Update: Why Raipur Billet Prices Are Shifting",
    snippet:
      "A deep dive into the recent fluctuations in the Central Indian sponge iron market and what it means for your procurement strategy this quarter.",
    date: "April 12, 2026",
    readTime: "6 min read",
    category: "Market Insights",
    gradient: "from-info/30 to-primary/20",
    image: imgRaipur,
  },
  {
    slug: "escrow-vs-pdc",
    title: "Escrow vs. Post-Dated Cheques: Securing Your Yard's Cash Flow",
    snippet:
      "Scared of loading a truck and never getting paid? We break down why RBI-compliant nodal escrow accounts are replacing PDCs and letters of credit for scrap yard owners.",
    date: "April 5, 2026",
    readTime: "7 min read",
    category: "Finance",
    gradient: "from-success/30 to-accent/20",
    image: imgEscrowPdc,
  },
  {
    slug: "hms1-volatility-2026",
    title: "Navigating HMS 1 Price Volatility in 2026",
    snippet:
      "From global container rates to domestic furnace demand — the macro forces shaping HMS 1 pricing this year, and how to lock in margins.",
    date: "March 28, 2026",
    readTime: "5 min read",
    category: "Pricing",
    gradient: "from-warning/30 to-info/20",
    image: imgHmsVolatility,
  },
  {
    slug: "escrow-mandatory-foundries",
    title: "Why Escrow is Mandatory for Foundries Going into FY27",
    snippet:
      "Compliance officers at major foundries are mandating escrow-only procurement. Here's what your sales team needs to know to keep winning orders.",
    date: "March 20, 2026",
    readTime: "6 min read",
    category: "Compliance",
    gradient: "from-accent/30 to-primary/20",
    image: imgEscrowCompliance,
  },
];

export type BlogArticle = {
  slug: string;
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  category: string;
  date: string;
  readTime: string;
  materialLabel: string;
  marketplaceQuery: string;
  heroImage: string;
  body: { heading: string; paragraphs: string[] }[];
};

export const SITE = "https://lohalink.in";
export const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;

export const BLOG_ARTICLES: Record<string, BlogArticle> = {
  "epr-credit-brokerage-india-guide": {
    slug: "epr-credit-brokerage-india-guide",
    title: "EPR Credit Brokerage in India: The 2026 Playbook — LohaLink",
    description:
      "How EPR works in India for plastic, e-waste, battery and tyre waste: CPCB targets, certificate transfer and credit prices per tonne.",
    ogTitle: "EPR Credit Brokerage in India — The 2026 Compliance Playbook",
    ogDescription:
      "Category-wise obligation modelling, verified recyclers, escrow-protected credit purchase and audit-ready documentation for every PIBO.",
    category: "Compliance · EPR",
    date: "June 13, 2026",
    readTime: "8 min read",
    materialLabel: "EPR credits",
    marketplaceQuery: "/epr",
    heroImage: imgEprBrokerage,
    body: [
      {
        heading: "EPR is no longer a paperwork exercise",
        paragraphs: [
          "Extended Producer Responsibility (EPR) in India has moved from a compliance formality to a hard, portal-enforced financial liability. Producers, Importers and Brand Owners (PIBOs) must declare the quantity of packaging or product they place on the market, accept an escalating category-wise recycling target, and discharge it with EPR certificates generated by registered recyclers on the CPCB portal.",
          "The regimes now in force cover plastic packaging under the Plastic Waste Management Rules, electronics under the E-Waste (Management) Rules 2022, batteries under the Battery Waste Management Rules 2022, and waste tyre and used oil under the Hazardous & Other Wastes framework. Each has its own categories, EEE codes, recovery targets and credit pool.",
        ],
      },
      {
        heading: "Where PIBOs actually get caught",
        paragraphs: [
          "Almost no one fails EPR because they refused to comply. They fail on execution. The three recurring failure modes we see are: buying credits from a recycler whose registration or category authorisation does not cover the waste stream claimed; certificates transferred against the wrong category, EEE code or financial year, leaving the obligation technically open; and Q4 panic buying, when supply thins out and prices can double.",
          "Environmental Compensation is deliberately punitive — typically a multiple of the prevailing credit price — and the unmet obligation still carries forward. Paying EC is always the most expensive way to comply.",
        ],
      },
      {
        heading: "What EPR credits cost per tonne",
        paragraphs: [
          "Pricing is a function of waste stream, category, target-year supply and how early you contract. Indicative bands in the current market: plastic waste ₹4,000–₹12,000 per tonne (Category II and III are tightest), e-waste ₹12,000–₹32,000 per tonne depending on EEE code, battery waste ₹9,000–₹26,000 per tonne by chemistry, waste tyre ₹3,500–₹9,000 per tonne and used oil ₹3,000–₹8,000 per tonne.",
          "A PIBO that contracts its basket in the first half of the financial year routinely lands 20–35% below what the same tonnage costs in the closing quarter. Timing is the single largest controllable variable in EPR cost.",
        ],
      },
      {
        heading: "How brokerage should work",
        paragraphs: [
          "A credible EPR broker does four things. It validates each recycler against CPCB/SPCB registration, category authorisation and installed capacity before it enters the sourcing pool. It models your liability across categories and financial years, including carry-forward, and converts it into a procurement calendar. It reconciles every certificate transfer line-by-line against your declared category and year on the portal. And it holds payment until the credits actually land in your account.",
          "LohaLink runs EPR settlement on the same rails that secure our bulk metal trades: GST and KYC verification of counterparties, RBI-grade nodal escrow that releases only on verified fulfilment, GPS-tracked dispatch and digital weighbridge records where physical material moves, and a documentation pack formatted for annual returns, ESG assurance and BRSR disclosure.",
        ],
      },
      {
        heading: "Build the obligation plan before the year, not after it",
        paragraphs: [
          "Model your category-wise tonnage, budget it at realistic per-tonne bands, and contract supply progressively. Our multi-material estimator on the EPR page gives you a directional annual budget in under a minute, and our EPR desk turns it into a firm proposal within one business day. Metal Linked.",
        ],
      },
    ],
  },
  "mid-year-market-report-iron-scrap-volatility-2026": {
    slug: "mid-year-market-report-iron-scrap-volatility-2026",
    title:
      "Iron Scrap Price Volatility 2026 — LohaLink Report",
    description:
      "Mid-year 2026 report on Indian iron scrap pricing: HMS demand, supply bottlenecks, AI price benchmarking and how escrow protects margins from market swings.",
    ogTitle: "Iron Scrap Price Volatility in India — Mid-Year 2026 Report",
    ogDescription:
      "Live AI price benchmarking, transparent state-by-state discovery and escrow-secured trade for India's bulk scrap metal market in 2026.",
    category: "Market Report · Iron Prices",
    date: "April 30, 2026",
    readTime: "5 min read",
    materialLabel: "AI-benchmarked HMS lots",
    marketplaceQuery: "/buyer?q=HMS%201",
    heroImage: imgIronVolatility2026,
    body: [
      {
        heading: "The Current Pricing Landscape",
        paragraphs: [
          "As we move through 2026, the Indian bulk scrap metal market continues to experience dynamic price shifts. Driven by aggressive domestic infrastructure spending, demand for heavy melting scrap remains robust.",
          "However, localized supply chain bottlenecks often cause sudden daily price swings, leaving unorganized traders vulnerable to lost margins and missed dispatches.",
        ],
      },
      {
        heading: "Data-Driven Trading Over Speculation",
        paragraphs: [
          "Relying on a broker's \"word of mouth\" for the daily rate is a fast track to lost margins. LohaLink provides users with institutional-grade pricing intelligence built for the modern Indian metal trade.",
          "Live AI Price Benchmarking analyzes data across regional markets to display true, real-time market rates for HMS 1, HMS 2, sponge iron and MS billets.",
          "Transparent Discovery means sellers can see exactly what foundries are currently paying in different states — no more guessing the fair rate.",
          "Locked-in Escrow protects both parties when prices swing. Our RBI-grade nodal escrow eliminates the risk of sudden market-rate defaults and delayed payments.",
        ],
      },
      {
        heading: "Trade with confidence in 2026",
        paragraphs: [
          "Leverage LohaLink's data to secure the best price for your material today. Metal Linked.",
        ],
      },
    ],
  },
  "india-scrap-market-organizing-300-mt": {
    slug: "india-scrap-market-organizing-300-mt",
    title:
      "India's Scrap Market is Organizing — LohaLink",
    description:
      "India targets 300 MT of crude steel by 2030 with a push to secondary steel. How LohaLink formalizes the 70% unorganized scrap network with KYC, GST and escrow.",
    ogTitle: "India's Multi-Billion Dollar Scrap Market is Organizing",
    ogDescription:
      "Digital consolidation, GST compliance and instant escrow payments — LohaLink is the operating system for India's secondary steel transition.",
    category: "Industry Growth · Secondary Steel",
    date: "April 29, 2026",
    readTime: "6 min read",
    materialLabel: "organized scrap network",
    marketplaceQuery: "/auth",
    heroImage: imgMarketOrganizing,
    body: [
      {
        heading: "Scaling to Meet the 300 MT Target",
        paragraphs: [
          "India is aggressively targeting a crude steel production capacity of 300 million tonnes by 2030, heavily pushing secondary steel production. This policy shift has turned metal recycling into a multi-billion dollar industry.",
          "Yet, over 70% of the scrap collection network remains in the unorganized sector — a structural bottleneck that limits how fast corporate foundries can scale.",
        ],
      },
      {
        heading: "The Bottleneck of the Unorganized Sector",
        paragraphs: [
          "As corporate foundries scale up, they cannot source from an untraceable network. They require GST compliance, consistent quality grading, and auditable payment trails. The traditional broker-led supply chain simply cannot meet this bar.",
        ],
      },
      {
        heading: "LohaLink: Organizing the Unorganized",
        paragraphs: [
          "LohaLink is capturing this massive market opportunity by giving MSME scrap yards enterprise-grade tools that were previously reserved for large corporates.",
          "100% Tax & KYC Compliance standardizes the base of the supply chain — every yard is GSTIN and PAN verified before listing.",
          "Digital Consolidation aggregates thousands of verified yards into a single dashboard for foundry procurement teams.",
          "Financial Formalization moves transactions to secure, instant digital escrow payments — replacing PDCs and cash with auditable, RBI-grade settlement.",
        ],
      },
      {
        heading: "The operating system for the transition",
        paragraphs: [
          "The Indian scrap market is maturing. LohaLink is the operating system for this transition. Metal Linked.",
        ],
      },
    ],
  },
  "supplying-indias-emerging-foundry-hubs": {
    slug: "supplying-indias-emerging-foundry-hubs",
    title:
      "Supplying India's Emerging Foundry Hubs — LohaLink",
    description:
      "New electric arc furnaces are opening across Eastern India. How LohaLink connects MSME yards to pan-India foundry demand with escrow.",
    ogTitle: "New Furnaces, New Opportunities — Pan-India Foundry Sourcing",
    ogDescription:
      "Discover newly verified foundries, dispatch with RBI-grade escrow protection and track every truck pan-India on LohaLink.",
    category: "Pan-India Trade · Logistics",
    date: "April 28, 2026",
    readTime: "5 min read",
    materialLabel: "pan-India foundry demand",
    marketplaceQuery: "/buyer",
    heroImage: imgEmergingHubs,
    body: [
      {
        heading: "The Geographic Shift in Steel Production",
        paragraphs: [
          "The landscape of Indian manufacturing is shifting. Driven by state incentives, we are seeing a surge of new electric arc furnaces opening in emerging industrial hubs across Eastern India and Western manufacturing corridors.",
          "These new furnaces represent fresh, hungry demand for high-quality scrap — but they sit outside the traditional broker network of any single yard.",
        ],
      },
      {
        heading: "The Challenge of Reaching New Buyers",
        paragraphs: [
          "For a scrap yard owner, a new foundry 400 kilometers away represents a massive opportunity. However, tapping into that demand requires two hard things: discovering the buyer in the first place, and trusting them enough to dispatch a truck full of material.",
          "Without a verified network, expansion is risky. One bad payment cycle can wipe out a quarter of margins.",
        ],
      },
      {
        heading: "Pan-India Sourcing with LohaLink",
        paragraphs: [
          "LohaLink breaks down geographic borders for MSME metal traders.",
          "Discover New Demand by connecting your inventory directly to newly verified foundries across the country.",
          "Risk-Free Expansion through RBI-grade Escrow ensures your payment is secured before the truck leaves your yard.",
          "Logistics Integration with our fleet partners ensures total visibility of freight from dispatch to delivery — both buyer and seller see every kilometer.",
        ],
      },
      {
        heading: "Your market is now national",
        paragraphs: [
          "Your market is no longer just your local district. Metal Linked.",
        ],
      },
    ],
  },
  "malda-asansol-hms1-case-study": {
    slug: "malda-asansol-hms1-case-study",
    title:
      "Malda to Asansol — 50T HMS 1 in 48 Hours — LohaLink",
    description:
      "How a Malda yard and an Asansol foundry closed a 50-tonne HMS 1 deal on LohaLink in 48 hours — AI-verified quality, RBI-grade escrow, same-day settlement.",
    ogTitle: "Malda → Asansol: 50T HMS 1 in 48 Hours — LohaLink Case Study",
    ogDescription:
      "Bridging North and South Bengal: a real LohaLink success story with zero brokerage, zero quality disputes and zero payment risk.",
    category: "Case Study · West Bengal",
    date: "April 28, 2026",
    readTime: "7 min read",
    materialLabel: "HMS 1 lots",
    marketplaceQuery: "/buyer?q=HMS%201",
    heroImage: imgMaldaAsansol,
    body: [
      {
        heading: "Bridging the Distance: A Seamless Trade Story between North and South Bengal",
        paragraphs: [
          "In the traditional scrap market, a deal between a yard in Malda and a foundry in Asansol would usually involve at least two brokers, three days of price haggling, and a week of payment uncertainty.",
          "Here is how LohaLink turned that complex journey into a 48-hour success story.",
        ],
      },
      {
        heading: "The Players",
        paragraphs: [
          "The Seller: A GST-verified yard owner in Malda looking to reach buyers beyond his local district to get a fair market rate.",
          "The Buyer: A sourcing manager for a steel foundry in Asansol needing a consistent, high-quality supply of Heavy Melting Scrap (HMS 1) to keep production lines running.",
        ],
      },
      {
        heading: "Step 1: The 60-Second Listing (Malda)",
        paragraphs: [
          "The yard owner in Malda opened the LohaLink Supplier Central app. He snapped a photo of his 50-tonne HMS 1 lot. Our AI Vision tool instantly categorized the scrap, confirming its grade.",
          "He dropped a GPS pin, set his price based on the AI Price Benchmark, and hit \"List.\"",
        ],
      },
      {
        heading: "Step 2: Instant Discovery (Asansol)",
        paragraphs: [
          "Over 200 kilometers away, the Procurement Manager in Asansol was browsing the Foundry Connect portal. He filtered for \"HMS 1\" within a specific radius and saw the Malda lot.",
          "He could see the photo-verified quality and the seller's \"GST-Verified\" badge. No phone calls were needed to verify if the seller was legitimate.",
        ],
      },
      {
        heading: "Step 3: The Digital Handshake & Escrow",
        paragraphs: [
          "The buyer liked the price and the quality. With one click, he placed the order. To secure the deal, the foundry's finance team locked the funds in LohaLink's RBI-grade nodal escrow.",
          "For the Malda Seller: He received an instant notification that his payment was secured. He could load the trucks with 100% confidence that the money was already \"in the system.\"",
          "For the Asansol Buyer: He knew his money wouldn't be touched until the trucks actually reached his gate.",
        ],
      },
      {
        heading: "Step 4: Transparent Fulfillment",
        paragraphs: [
          "The trucks were dispatched from Malda. Through LohaLink's integrated logistics, the buyer in Asansol tracked the trucks in real-time. Upon arrival, the digital weighbridge slips were uploaded to the platform.",
        ],
      },
      {
        heading: "The Result: Settlement in Hours, Not Weeks",
        paragraphs: [
          "Once the weighbridge data was confirmed by both parties, the escrow funds were released. The yard owner in Malda received his payment the very same evening — a process that used to take 15+ days through traditional channels.",
          "The LohaLink Difference: Zero Brokerage (no hidden middleman tax). Zero Quality Disputes (AI-photo verification meant what was seen in Malda was exactly what arrived in Asansol). Zero Payment Risk (escrow protected both buyer capital and seller cash flow).",
          "Ready to write your own success story? Whether you are buying or selling, join the network that is making the Indian metal trade faster, safer, and better. Metal Linked.",
        ],
      },
    ],
  },
  "rbi-grade-escrow-future-of-bulk-scrap-trading": {
    slug: "rbi-grade-escrow-future-of-bulk-scrap-trading",
    title:
      "RBI-Grade Escrow for Bulk Scrap Trade — LohaLink",
    description:
      "The Indian scrap trade runs on 'Maal gaya, paisa kab?' How RBI-grade nodal escrow guarantees same-day settlement and quality assurance on LohaLink.",
    ogTitle: "RBI-Grade Escrow — The Future of Bulk Scrap Metal Trading in India",
    ogDescription:
      "Replacing financial risk in India's scrap trade with regulated nodal escrow. Same-day payouts, guaranteed quality holds, zero PDC bounces.",
    category: "Finance · Escrow",
    date: "April 27, 2026",
    readTime: "6 min read",
    materialLabel: "escrow-secured trade",
    marketplaceQuery: "/auth",
    heroImage: imgEscrowSecure,
    body: [
      {
        heading: "The Problem: \"Maal Gaya, Paisa Kab?\"",
        paragraphs: [
          "For decades, the Indian scrap metal trade has operated on an uneasy balance of verbal commitments and high-risk credit. A yard owner in Siliguri dispatches 50 tonnes of HMS 1 to a foundry in Durgapur. The truck arrives, the material is unloaded, but the payment? That might take 15, 30, or even 45 days. Meanwhile, the yard's working capital is blocked.",
          "On the flip side, buyers face their own risks. What if an advance payment is made, but the delivered scrap is mixed with heavy impurities or doesn't match the agreed-upon grade?",
          "This trust deficit limits growth for everyone.",
        ],
      },
      {
        heading: "The Solution: Escrow-Protected Trading",
        paragraphs: [
          "At LohaLink, we realized that replacing the broker wasn't enough; we had to replace the financial risk. That is why every transaction on our platform is backed by an RBI-grade Nodal Escrow account.",
        ],
      },
      {
        heading: "For the Seller: Guaranteed Settlement",
        paragraphs: [
          "When an order is placed, the buyer's funds are immediately locked in the escrow account. You know the money is safe and waiting.",
          "Once your truck reaches the weighbridge and the buyer accepts the material, the funds are released to your account the same day. No more chasing payments.",
        ],
      },
      {
        heading: "For the Buyer: Quality Assurance",
        paragraphs: [
          "You never pay for sub-standard material. Your funds are only released to the supplier after the truck has arrived at your gate and you have verified the quality and weight against the digital weighbridge slip.",
        ],
      },
      {
        heading: "Growth Through Security",
        paragraphs: [
          "By removing the anxiety of delayed payments and quality disputes, businesses can focus on what actually matters: scaling their operations. At LohaLink, we believe that secure payments are the foundation of a modern, organized supply chain.",
          "Metal Linked.",
        ],
      },
    ],
  },
  "bypassing-the-broker-foundry-direct-sourcing": {
    slug: "bypassing-the-broker-foundry-direct-sourcing",
    title:
      "Foundry-Direct Scrap Sourcing — LohaLink",
    description:
      "Foundry procurement teams can browse 3,400+ GST-verified yards, AI-benchmarked prices and live truck tracking on LohaLink's Foundry Connect portal.",
    ogTitle: "Bypass the Broker — Foundry-Direct Scrap Sourcing on LohaLink",
    ogDescription:
      "AI-benchmarked pricing, GST-verified suppliers, live logistics. The foundry procurement playbook for 2026.",
    category: "Procurement · Foundry",
    date: "April 26, 2026",
    readTime: "6 min read",
    materialLabel: "Foundry Connect",
    marketplaceQuery: "/buyer",
    heroImage: imgFoundryDirect,
    body: [
      {
        heading: "The Hidden Cost of the Traditional Supply Chain",
        paragraphs: [
          "If you manage procurement for a steel plant or foundry, you know the drill. Finding a consistent, high-quality supply of scrap metal usually involves navigating a web of brokers and middlemen. Each layer adds a margin to the price, delays the communication, and muddies the transparency of where your material is actually coming from.",
          "When a critical shortage hits, relying on an unverified network can bring production to a grinding halt.",
        ],
      },
      {
        heading: "Direct Connect: The LohaLink Advantage",
        paragraphs: [
          "LohaLink's Foundry Connect portal was built to flatten this supply chain. Instead of calling three different brokers to source scrap from across the state, procurement officers can now log into a single digital dashboard and browse inventory from over 3,400 GST-verified yards.",
        ],
      },
      {
        heading: "AI-Benchmarked Pricing",
        paragraphs: [
          "Stop guessing the fair market rate. LohaLink's AI analyzes live market trends to give you accurate price benchmarks for grades like HMS 1, ensuring you always negotiate from a position of strength.",
        ],
      },
      {
        heading: "100% Tax Compliant Network",
        paragraphs: [
          "Every supplier on LohaLink undergoes multi-level KYC and GSTIN verification. You get clean invoices every time, making your GST filing seamless and automatic.",
        ],
      },
      {
        heading: "Live Logistics Visibility",
        paragraphs: [
          "Once you lock in a lot, you aren't left in the dark. Pan-India fleet integration gives you live truck tracking from the seller's yard directly to your weighbridge.",
          "It is time to take control of your raw material sourcing. Connect directly, negotiate transparently, and build a supply chain you can rely on.",
        ],
      },
    ],
  },
  "gst-digital-invoicing-scrap-traders": {
    slug: "gst-digital-invoicing-scrap-traders",
    title:
      "GST & Digital Invoicing for Scrap Traders — LohaLink",
    description:
      "GST compliance, GSTIN verification and automated e-invoicing for India's bulk scrap metal traders. How LohaLink eliminates blocked ITC and paperwork.",
    ogTitle: "GST for Scrap Metal Traders — Digital Invoicing on LohaLink",
    ogDescription:
      "100% verified ecosystem, automated GST invoices and seamless ITC claims for the Indian scrap metal trade.",
    category: "Compliance · GST",
    date: "April 24, 2026",
    readTime: "6 min read",
    materialLabel: "verified GST trade",
    marketplaceQuery: "/auth",
    heroImage: imgGstDigital,
    body: [
      {
        heading: "The Compliance Headache",
        paragraphs: [
          "For years, the bulk scrap metal trade operated largely in the unorganized sector. But as the industry modernizes, compliance is no longer optional — it is a prerequisite for growth.",
          "For local iron traders and yard owners, managing GST filings, verifying buyer GSTINs, and generating e-invoices can feel like a full-time job that distracts from core operations: sourcing and moving scrap metal.",
          "Mismatched invoices or dealing with unverified parties can lead to blocked Input Tax Credit (ITC) and severe penalties, squeezing already tight margins.",
        ],
      },
      {
        heading: "Automating the Paperwork with LohaLink",
        paragraphs: [
          "Transitioning to a fully compliant model shouldn't mean drowning in paperwork. LohaLink was built to automate the heavy lifting of B2B compliance.",
        ],
      },
      {
        heading: "100% Verified Ecosystem",
        paragraphs: [
          "Every participant on the LohaLink platform — from a local iron trader to a massive steel foundry — must pass rigorous GSTIN and KYC verification before their account is activated. You never have to worry if the business you are trading with is legitimate.",
        ],
      },
      {
        heading: "Automated Invoicing",
        paragraphs: [
          "Once a deal is struck and the material is delivered, the platform automatically generates GST-compliant invoices — no manual data entry, no duplicated paperwork.",
        ],
      },
      {
        heading: "Seamless ITC Claims",
        paragraphs: [
          "Clean, digital records mean your accounting team can process ITC claims without chasing down physical receipts or correcting manual entry errors.",
          "By standardizing the compliance process, LohaLink allows you to trade freely across state lines with complete regulatory peace of mind.",
          "Metal Linked.",
        ],
      },
    ],
  },
  "3-ways-ai-upgrading-indian-scrap-yard": {
    slug: "3-ways-ai-upgrading-indian-scrap-yard",
    title:
      "3 Ways AI is Upgrading the Indian Scrap Yard — LohaLink",
    description:
      "AI photo categorization, 60-second mobile listings and digital LR receipts — how India's scrap yards are leading the digital circular economy with LohaLink.",
    ogTitle: "3 Ways AI is Upgrading the Indian Scrap Yard",
    ogDescription:
      "Mobile-first listings, AI vision grading, and fully digital record keeping for India's metal recycling sector.",
    category: "Technology · AI Vision",
    date: "April 25, 2026",
    readTime: "5 min read",
    materialLabel: "Supplier Central",
    marketplaceQuery: "/auth",
    heroImage: imgAiYardUpgrade,
    body: [
      {
        heading: "From rusted scales to a data-driven yard",
        paragraphs: [
          "The scrap yard of the past was defined by rusted scales, paper ledgers, and constant phone calls. The scrap yard of the future is mobile-first, data-driven, and highly connected.",
          "As India pushes toward a more robust circular economy, the recycling of iron and steel is taking center stage. To meet the massive demand from domestic foundries, scrap suppliers are turning to technology to work faster and smarter.",
        ],
      },
      {
        heading: "1. AI Photo Categorization",
        paragraphs: [
          "Disputes over scrap grades are a major industry headache. LohaLink's Supplier Central app features an AI Vision tool. Sellers simply snap a photo of their lot, and the AI instantly suggests the correct category and grade.",
          "This drastically reduces quality disputes upon delivery, as both buyer and seller have a transparent, photo-verified baseline before the truck even leaves the yard.",
        ],
      },
      {
        heading: "2. Mobile-First Upload Flows",
        paragraphs: [
          "We know that yard owners don't sit behind a desk all day. They are out in the field. That's why listing inventory on LohaLink takes exactly 60 seconds from a smartphone.",
          "Snap a photo, drop a GPS pin, set your price, and your material is instantly visible to over 850 verified buyers.",
        ],
      },
      {
        heading: "3. Digital Record Keeping",
        paragraphs: [
          "Lost weighbridge slips and messy paper trails are a thing of the past. From digital LR receipts to automated, GST-clean invoices generated the moment an escrow payment is settled, LohaLink digitizes the entire administrative backend of scrap trading.",
          "The industry is evolving rapidly. By adopting these digital tools, scrap yards aren't just surviving the transition — they are leading it.",
        ],
      },
    ],
  },
  "hms-1-scrap-buyers-guide-india": {
    slug: "hms-1-scrap-buyers-guide-india",
    title:
      "HMS 1 Scrap in India — Buyer's Guide 2026 — LohaLink",
    description:
      "Buyer's guide to HMS 1 scrap in India: chemistry, density, price drivers, and how to source from GST-verified dealers with escrow protection on LohaLink.",
    ogTitle: "HMS 1 Scrap in India — Buyer's Guide (2026)",
    ogDescription:
      "Specs, pricing and verified HMS 1 suppliers across Durgapur, Kolkata, Raipur and Mandi Gobindgarh — escrow-protected on LohaLink.",
    category: "Materials · HMS 1",
    date: "April 18, 2026",
    readTime: "8 min read",
    materialLabel: "HMS 1 scrap",
    marketplaceQuery: "/buyer?q=HMS%201",
    heroImage: imgHms1,
    body: [
      {
        heading: "What qualifies as HMS 1 scrap in India?",
        paragraphs: [
          "Heavy Melting Scrap Grade 1 (HMS 1) is wrought iron and/or steel scrap with sections at least ¼ inch (6 mm) thick, free of non-ferrous attachments, sealed containers and excessive rust. In Indian induction-furnace practice, foundries typically expect a minimum density of ~1.0 t/m³ and a maximum of 1.5% non-metallic contamination.",
          "Confusion between HMS 1 and HMS 1 & 2 (80:20) is the single biggest cause of weighbridge disputes. LohaLink standardises grade descriptions across every yard and runs AI scrap metal grading on yard photos before listing.",
        ],
      },
      {
        heading: "What drives HMS 1 prices in India?",
        paragraphs: [
          "Domestic HMS 1 pricing tracks four levers: container scrap landed cost (Nhava Sheva / Mundra), domestic billet trends in Raipur and Mandi Gobindgarh, sponge iron (DRI) availability as a substitute, and rupee-dollar movement.",
          "When DRI prices spike, induction furnaces lean harder on HMS 1 — pulling regional prices up by ₹500–₹1,500/tonne within days. Tracking these signals weekly on the LohaLink Journal helps procurement teams time their lots.",
        ],
      },
      {
        heading: "How to source HMS 1 from verified suppliers",
        paragraphs: [
          "Every HMS 1 listing on LohaLink comes from a GST-verified yard with PAN + bank penny-drop verification. Funds sit in an RBI-compliant nodal escrow until the buyer's weighbridge confirms the load, eliminating PDC bounce risk.",
          "Read more about how escrow, GSTIN auto-verification and weighbridge dispute resolution work in our About FAQ — or jump straight to live HMS 1 listings on the marketplace.",
        ],
      },
    ],
  },
  "sponge-iron-dri-procurement-playbook": {
    slug: "sponge-iron-dri-procurement-playbook",
    title:
      "Sponge Iron (DRI) Procurement Playbook — LohaLink",
    description:
      "Coal-based vs gas-based sponge iron, Fe(M) and metallization benchmarks, and how to lock in verified DRI suppliers from Durgapur, Raipur and Jharsuguda.",
    ogTitle: "Sponge Iron (DRI) Procurement Playbook — India 2026",
    ogDescription:
      "Specs, metallization, and verified DRI suppliers across India's major sponge iron hubs — escrow-secured on LohaLink.",
    category: "Materials · Sponge Iron (DRI)",
    date: "April 15, 2026",
    readTime: "9 min read",
    materialLabel: "Sponge Iron (DRI)",
    marketplaceQuery: "/buyer?q=Sponge%20Iron",
    heroImage: imgSpongeIron,
    body: [
      {
        heading: "Coal-based vs gas-based sponge iron",
        paragraphs: [
          "Indian induction furnaces are dominated by coal-based sponge iron (DRI) produced in rotary kilns across Durgapur, Raipur, Jharsuguda and Bellary. Gas-based DRI is more common in West India and tends to carry higher metallization (92–94%) compared to the typical coal-based 80–86% range.",
          "Choosing the right grade depends on your furnace power profile and slag chemistry. LohaLink's listings expose Fe(T), Fe(M), metallization % and sulphur from the seller's lab report up-front.",
        ],
      },
      {
        heading: "Pricing and procurement strategy",
        paragraphs: [
          "DRI prices move with thermal coal, iron ore lump availability and downstream billet demand. Foundries that lock in 30–60 day rolling contracts with verified sponge iron suppliers consistently beat spot buyers by ₹800–₹1,200/tonne.",
          "On LohaLink, buyers can filter by hub (Durgapur, Raipur, Jharsuguda), metallization band, and seller rating — then settle through escrow with no PDC exposure.",
        ],
      },
      {
        heading: "Why escrow matters more for DRI",
        paragraphs: [
          "DRI degrades on exposure to moisture, so re-handling delays from payment disputes are doubly expensive. Escrow-backed dispatch keeps the truck moving, the buyer protected on quality, and the seller paid within 24 hours of weighbridge confirmation.",
        ],
      },
    ],
  },
  "ai-vision-resolving-scrap-quality-disputes": {
    slug: "ai-vision-resolving-scrap-quality-disputes",
    title:
      "AI Vision for Scrap Quality Disputes — LohaLink",
    description:
      "Tired of grade downgrades on delivery? How LohaLink uses AI Vision to standardize scrap categorization and eliminate quality disputes before dispatch.",
    ogTitle: "AI Scrap Sorting in India — Solve HMS 1 vs HMS 2 Disputes",
    ogDescription:
      "LohaLink's AI Vision standardizes scrap grading from a smartphone photo — ending the 'I sent HMS 1, you graded it HMS 2' argument for good.",
    category: "Technology · AI Vision",
    date: "April 22, 2026",
    readTime: "5 min read",
    materialLabel: "AI-graded scrap",
    marketplaceQuery: "/auth",
    heroImage: imgAiVision,
    body: [
      {
        heading: "\"I Sent HMS 1, You Graded it HMS 2\" – Solving the Industry's Oldest Argument with AI",
        paragraphs: [
          "If you are a scrap aggregator, you know the sinking feeling. Your truck arrives at the foundry, and suddenly, the buyer claims your premium Heavy Melting Scrap (HMS 1) is actually a lower grade.",
          "Price renegotiations begin. Margins vanish. You either accept the lower rate or pay to haul your material back. In 2026, relying on \"visual estimates\" by the unloading crew is no longer a viable business strategy.",
        ],
      },
      {
        heading: "Enter LohaLink's AI Vision",
        paragraphs: [
          "We built LohaLink to take subjective human bias out of the equation. Our platform utilizes advanced AI auto-categorization straight from your smartphone.",
          "For Sellers: Snap a photo of your lot in 60 seconds. Our AI suggests the exact category based on visual data, establishing a baseline before the truck ever leaves your yard.",
          "For Buyers: You get an objective, benchmarked view of the material you are buying, reducing the need for hostile price haggling at the gate.",
        ],
      },
      {
        heading: "Trust, standardised",
        paragraphs: [
          "Trade shouldn't be an argument. By standardizing quality through technology, we bring trust back to the yard.",
          "Ready to list your material with AI-backed confidence? Enter Supplier Central and start uploading lots today.",
        ],
      },
    ],
  },
  "why-escrow-is-critical-for-scrap-trade-2026": {
    slug: "why-escrow-is-critical-for-scrap-trade-2026",
    title:
      "Why Escrow is Critical for Scrap Trade in 2026 — LohaLink",
    description:
      "Cash flow is king in 2026. How LohaLink's RBI-compliant escrow protects scrap aggregators from delayed payments and bounced PDCs.",
    ogTitle: "RBI-Compliant Escrow for Indian Scrap Trade — LohaLink",
    ogDescription:
      "Stop chasing PDCs. LohaLink's RBI-compliant nodal escrow guarantees payment the moment your truck is approved at the gate.",
    category: "Finance · Escrow",
    date: "April 20, 2026",
    readTime: "6 min read",
    materialLabel: "escrow-secured trade",
    marketplaceQuery: "/auth",
    heroImage: imgEscrowSecure,
    body: [
      {
        heading: "Why Your Metal Business Cannot Afford Another Bounced PDC in 2026",
        paragraphs: [
          "As we move through Q2 2026, the iron and steel supply chain is moving faster than ever, but working capital remains the biggest bottleneck.",
          "For decades, the Indian scrap trade has operated on blind trust and Post-Dated Cheques (PDCs). You load millions of rupees worth of Sponge Iron or Billets onto a truck, hand it over, and cross your fingers. One delayed payment or bounced cheque doesn't just hurt your profit—it paralyzes your entire yard's ability to buy new stock.",
          "At LohaLink, our core philosophy is Metal Linked — connecting buyers and sellers so growth and abundance follow. You cannot have abundance if you are constantly chasing your own money.",
        ],
      },
      {
        heading: "The Power of the Escrow Lock",
        paragraphs: [
          "We integrated an RBI-compliant Escrow mechanism directly into the LohaLink platform.",
          "Buyers lock the funds in a secure nodal account with one click. Sellers dispatch the truck only after seeing the \"Funds Locked\" green signal on their dashboard. Upon successful delivery, funds are instantly routed to the seller.",
          "Stop funding other people's operations. Secure your cash flow and trade with zero financial anxiety.",
        ],
      },
      {
        heading: "Protect your next dispatch",
        paragraphs: [
          "Whether you're a yard owner tired of chasing cheques or a foundry that wants compliance-ready procurement, escrow is the new default. Register as a verified seller today and lock your next dispatch.",
        ],
      },
    ],
  },
  "direct-to-foundry-zero-middlemen-metal-trade": {
    slug: "direct-to-foundry-zero-middlemen-metal-trade",
    title:
      "Direct-to-Foundry Metal Trade — LohaLink",
    description:
      "Stop losing margins to broker commissions. LohaLink connects verified scrap suppliers directly with major foundries for transparent, escrow-secured trade.",
    ogTitle: "Sell Scrap Direct to Foundry — Zero-Commission B2B Metal Trade",
    ogDescription:
      "GST-verified suppliers and foundries trade directly on LohaLink — no brokers, no hidden commissions, escrow-secured.",
    category: "Marketplace · Direct Trade",
    date: "April 17, 2026",
    readTime: "5 min read",
    materialLabel: "direct foundry trade",
    marketplaceQuery: "/buyer",
    heroImage: imgFoundryDirect,
    body: [
      {
        heading: "Cutting the Fat: Why Direct-to-Foundry is the Only Way to Trade",
        paragraphs: [
          "Look at your last five transactions. How much of your margin was eaten up by middleman commissions, broker fees, and opaque price markups?",
          "In the unorganized metal sector, layers of intermediaries don't just eat into your profits; they distort the market price. Sellers get less than their material is worth, and foundries pay more than they should. It is a lose-lose scenario created by a lack of network access.",
        ],
      },
      {
        heading: "LohaLink is the direct bridge",
        paragraphs: [
          "We provide a One Platform, Two Portals ecosystem.",
          "Supplier Central: Aggregators get direct access to procurement managers at major foundries in Eastern India and beyond.",
          "Foundry Connect: Buyers browse privacy-safe, radius-only locations to source material directly from the yard.",
        ],
      },
      {
        heading: "Verified, double-blind, zero-commission",
        paragraphs: [
          "Because every user is 100% GST-verified via our automated API, you know exactly who you are trading with. Double-blind chats allow you to negotiate directly, agree on a price, and execute the trade with zero hidden commissions.",
          "It is time to take back your margins. Register today and start trading direct.",
        ],
      },
    ],
  },
  "ms-billets-direct-from-mills": {
    slug: "ms-billets-direct-from-mills",
    title:
      "MS Billets Direct from Mills 2026 — LohaLink",
    description:
      "How rolling mills procure MS billets online direct from manufacturers — IS 2830/2831 specs, size ranges and why escrow-backed orders are replacing PDCs.",
    ogTitle: "MS Billets Direct from Mills — 2026 Procurement Guide",
    ogDescription:
      "IS 2830/2831 MS billets sourced directly from Indian manufacturers, settled via RBI-compliant escrow on LohaLink.",
    category: "Materials · MS Billets",
    date: "April 10, 2026",
    readTime: "7 min read",
    materialLabel: "MS Billets",
    marketplaceQuery: "/buyer?q=MS%20Billets",
    heroImage: imgMsBillets,
    body: [
      {
        heading: "Specs that matter: IS 2830 vs IS 2831",
        paragraphs: [
          "Indian rolling mills typically procure MS billets to IS 2830 (carbon steel billets, blooms and slabs for re-rolling) or IS 2831 (specifications for free-cutting and case-hardening grades). Cross-section sizes range from 100×100 mm to 150×150 mm, with lengths of 3–6 metres.",
          "LohaLink listings expose chemistry (C, Mn, S, P), section size, length and mill test certificate — so procurement teams compare apples to apples without long phone calls.",
        ],
      },
      {
        heading: "Why direct-from-mill beats broker-led trades",
        paragraphs: [
          "Each broker layer adds ₹400–₹800/tonne and an extra week of payment friction. By verifying mills via GSTIN auto-verification and routing payment through escrow, LohaLink removes both the cost and the credit risk.",
          "Mills get a guaranteed-paid order; rolling mills get IS-compliant billets at transparent ex-mill rates.",
        ],
      },
      {
        heading: "Replacing PDCs with escrow",
        paragraphs: [
          "Post-dated cheques have historically been the lubricant of MS billet trade. They've also been the primary cause of bad debt. RBI-compliant nodal escrow gives sellers certainty and buyers a quality-hold window — see our About FAQ for the full mechanics.",
        ],
      },
    ],
  },
};

export const buildBlogArticleLd = (p: BlogArticle, heroUrl: string) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: p.title.replace(" — LohaLink", ""),
  description: p.description,
  datePublished: p.date,
  dateModified: p.date,
  inLanguage: "en-IN",
  articleSection: p.category,
  keywords: [p.materialLabel, p.category, "scrap metal India", "LohaLink"].join(", "),
  author: { "@type": "Organization", name: "LohaLink", url: SITE },
  publisher: {
    "@type": "Organization",
    name: "LohaLink",
    logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE}/blog/${p.slug}` },
  image: heroUrl,
  about: p.materialLabel,
});

export const buildBlogBreadcrumbLd = (p: BlogArticle) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Journal", item: `${SITE}/blog` },
    { "@type": "ListItem", position: 3, name: p.title.replace(" — LohaLink", ""), item: `${SITE}/blog/${p.slug}` },
  ],
});
